// Copyright (c) 2026 Aravind Adari. All rights reserved.

import dayjs from "dayjs";

export interface PeriodRange {
  label: string;
  start: string;
  end: string;
}

/**
 * Returns the number of paid holiday days earned based on working days in a month.
 * Thresholds: 24+ → 4, 18+ → 3, 12+ → 2, 1+ → 1, 0 → 0.
 */
export function getEarnedHolidays(workingDays: number): number {
  if (workingDays >= 24) return 4;
  if (workingDays >= 18) return 3;
  if (workingDays >= 12) return 2;
  if (workingDays >= 1) return 1;
  return 0;
}

/**
 * Returns the 12 months of a financial year (Apr–Mar) starting from startYear.
 * e.g. startYear=2025 → Apr 2025 … Mar 2026
 */
export function getFYMonths(startYear: number): PeriodRange[] {
  const months: PeriodRange[] = [];
  for (let i = 0; i < 12; i++) {
    const m = dayjs(`${startYear}-04-01`).add(i, "month");
    months.push({
      label: m.format("MMM YYYY"),
      start: m.format("YYYY-MM-DD"),
      end: m.endOf("month").format("YYYY-MM-DD"),
    });
  }
  return months;
}

/**
 * Returns the 4 quarters of a financial year starting from startYear (Apr-based).
 */
export function getQuarters(startYear: number): PeriodRange[] {
  return [
    {
      label: `Q1 (Apr-Jun ${startYear})`,
      start: `${startYear}-04-01`,
      end: `${startYear}-06-30`,
    },
    {
      label: `Q2 (Jul-Sep ${startYear})`,
      start: `${startYear}-07-01`,
      end: `${startYear}-09-30`,
    },
    {
      label: `Q3 (Oct-Dec ${startYear})`,
      start: `${startYear}-10-01`,
      end: `${startYear}-12-31`,
    },
    {
      label: `Q4 (Jan-Mar ${startYear + 1})`,
      start: `${startYear + 1}-01-01`,
      end: `${startYear + 1}-03-31`,
    },
  ];
}

/**
 * Returns the 2 half-years of a financial year starting from startYear.
 */
export function getHalfYears(startYear: number): PeriodRange[] {
  return [
    {
      label: `H1 (Apr-Sep ${startYear})`,
      start: `${startYear}-04-01`,
      end: `${startYear}-09-30`,
    },
    {
      label: `H2 (Oct ${startYear}-Mar ${startYear + 1})`,
      start: `${startYear}-10-01`,
      end: `${startYear + 1}-03-31`,
    },
  ];
}

/**
 * Calculates net salary for a single employee for a given month.
 * Pure function — no Firebase dependency.
 */
export interface PayrollInput {
  baseSalary: number;
  workingDays: number;         // total working days in month (Mon–Fri)
  workedDays: number;          // days employee actually attended
  overtimeHours: number;       // hours beyond 8h/day
  paidLeaveDays: number;       // approved leave days taken
  otMultiplier: number;        // default 1.5
  roleHolidayAllowance: number; // paid holiday days allowed (from config)
}

export interface PayrollResult {
  dailyRate: number;
  otAmount: number;
  leaveDeduction: number;
  totalSalary: number;
}

export function calculatePayroll(input: PayrollInput): PayrollResult {
  const {
    baseSalary,
    workingDays,
    workedDays,
    overtimeHours,
    paidLeaveDays,
    otMultiplier,
    roleHolidayAllowance,
  } = input;

  const scheduledHours = 8;
  const dailyRate = workingDays > 0 ? baseSalary / workingDays : 0;
  const allowedPaidDays = workingDays - roleHolidayAllowance;

  let excessLeaveDays = 0;
  if (paidLeaveDays > roleHolidayAllowance) {
    excessLeaveDays += paidLeaveDays - roleHolidayAllowance;
  }
  if (workedDays < allowedPaidDays) {
    excessLeaveDays += allowedPaidDays - workedDays;
  }

  const leaveDeduction = excessLeaveDays * dailyRate;
  const otFraction = overtimeHours / scheduledHours;
  const otAmount = otFraction * dailyRate * otMultiplier;
  const totalSalary = baseSalary + otAmount - leaveDeduction;

  return { dailyRate, otAmount, leaveDeduction, totalSalary };
}

/**
 * Validates that a set of timestamps is in the correct order:
 * clockIn < lunchOut < lunchIn < clockOut
 * Returns null if valid, or an error message string if invalid.
 */
export function validateTimestampOrder(times: {
  clockIn?: string;
  lunchOut?: string;
  lunchIn?: string;
  clockOut?: string;
}): string | null {
  const { clockIn, lunchOut, lunchIn, clockOut } = times;

  if (clockOut && !clockIn) return "Clock Out requires Clock In";
  if (lunchOut && !clockIn) return "Lunch Out requires Clock In";
  if (lunchIn && !lunchOut) return "Lunch In (back) requires Lunch Out (going) first";

  const toMin = (t?: string) => {
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const ci = toMin(clockIn);
  const lo = toMin(lunchOut);
  const li = toMin(lunchIn);
  const co = toMin(clockOut);

  if (ci !== null && lo !== null && lo <= ci) return "Lunch Out must be after Clock In";
  if (lo !== null && li !== null && li <= lo) return "Lunch In must be after Lunch Out";
  if (ci !== null && co !== null && co <= ci) return "Clock Out must be after Clock In";
  if (li !== null && co !== null && co <= li) return "Clock Out must be after Lunch In";

  return null;
}
